import {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getTrustedOnboardingProfileFields,
  getUserProps,
  setUserProps,
} from '@pharmaplan/common';

import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';

import { createOnboardingClientTools } from '../../components/SignupFlow/AiCompanion/clientTools';
import { toElevenLabsClientTools } from '../../components/SignupFlow/AiCompanion/elevenLabsClientTools';
import { validateManualProfessionalProfile } from '../../components/SignupFlow/AiCompanion/manualProfileValidation';
import {
  AiCompanionSessionContext,
  OnboardingRepository,
} from '../../components/SignupFlow/AiCompanion/types';
import useAiDocumentUpload from './useAiDocumentUpload';
import useAiOnboardingCompletion from './useAiOnboardingCompletion';
import useAiOnboardingState from './useAiOnboardingState';
import useAiProfileSubmission from './useAiProfileSubmission';
import { buildWorkflowDynamicVariables } from '../../components/SignupFlow/AiCompanion/workflowState';

interface IUseAiCompanionController {
  getConversationId: () => string | null;
  switchToManualMode: () => void;
  sessionContext: AiCompanionSessionContext;
  signupRequestId: string;
}

const useAiCompanionController = ({
  getConversationId,
  switchToManualMode,
  sessionContext,
  signupRequestId,
}: IUseAiCompanionController) => {
  const dispatch = useAppDispatch();
  const userProps = useAppSelector(getUserProps);
  const {
    repository,
    verifyPharmacistLicense,
  } = useAiOnboardingState({
    signupRequestId,
  });
  const repositoryRef = useRef<OnboardingRepository>(repository);
  const trustedProfileFieldsRef = useRef(
    getTrustedOnboardingProfileFields(sessionContext),
  );
  const [error, setError] = useState('');
  const [isVoiceMode, setIsVoiceMode] = useState(true);
  const [pendingClientToolCount, setPendingClientToolCount] = useState(0);
  const documentUpload = useAiDocumentUpload({
    signupRequestId,
  });
  const submitFinalProfile = useAiProfileSubmission({
    getConversationId,
    getProfilePictureFile: documentUpload.getProfilePictureFile,
  });
  const completion = useAiOnboardingCompletion();

  repositoryRef.current = repository;
  trustedProfileFieldsRef.current = getTrustedOnboardingProfileFields(
    sessionContext,
  );

  const handleProviderMutedChange = useCallback((isMuted: boolean) => {
    setIsVoiceMode(!isMuted);
  }, []);

  const getDynamicVariables = useCallback(
    async () =>
      buildWorkflowDynamicVariables(sessionContext),
    [sessionContext],
  );

  const updateProfileNames = useCallback((profileNames: {
    firstName: string;
    lastName: string;
  }) => {
    trustedProfileFieldsRef.current = {
      ...trustedProfileFieldsRef.current,
      ...profileNames,
    };
    dispatch(setUserProps({
      ...userProps,
      ...profileNames,
    }));
  }, [dispatch, userProps]);

  const handleClientToolStarted = useCallback(() => {
    setPendingClientToolCount((currentCount) =>
      currentCount + 1);
  }, []);

  const handleClientToolFinished = useCallback(() => {
    setPendingClientToolCount((currentCount) =>
      Math.max(0, currentCount - 1));
  }, []);

  const clientTools = useMemo(() => {
    const onboardingClientTools = createOnboardingClientTools({
      getRepository: () =>
        repositoryRef.current,
      onProfileSubmissionSettled: completion.handleProfileSubmissionSettled,
      openDocumentUpload: documentUpload.openDocumentUpload,
      signupRequestId,
      submitFinalProfile,
      switchToManualMode,
      getTrustedProfileFields: () =>
        trustedProfileFieldsRef.current,
      updateProfileNames,
      validateProfile: validateManualProfessionalProfile,
      verifyPharmacistLicense,
    });

    return toElevenLabsClientTools(onboardingClientTools, {
      onClientToolFinished: handleClientToolFinished,
      onClientToolStarted: handleClientToolStarted,
    });
  }, [
    completion.handleProfileSubmissionSettled,
    documentUpload.openDocumentUpload,
    handleClientToolFinished,
    handleClientToolStarted,
    signupRequestId,
    submitFinalProfile,
    switchToManualMode,
    updateProfileNames,
    verifyPharmacistLicense,
  ]);

  return {
    clientTools,
    documentUploadDialogProps: documentUpload.dialogProps,
    getDynamicVariables,
    handleConversationEnded: completion.handleConversationEnded,
    handleProviderMutedChange,
    isClientToolProcessing: pendingClientToolCount > 0,
    isVoiceMode,
    setError,
    setIsVoiceMode,
    visibleError: error,
  };
};

export default useAiCompanionController;
